import { APP_VERSION, versionCode } from '../version';
import { apiBase, isNative } from './host';

/**
 * Whether a newer application has been published on the site.
 *
 * There is no store account and one workshop to reach, so the site is the
 * distribution channel: `release/` on the server is served at `/app/`, and
 * `latest.json` beside the APK says what is there. The application compares it
 * with the version it was built from and says so — once, quietly, after signing
 * in, because an update prompt in front of somebody who cannot get past the
 * login screen is a prompt in the way.
 *
 * Only in the application. A browser gets whatever the server serves the moment
 * it reloads and has nothing to update.
 */

export interface Release {
  version: string;
  versionCode: number;
  /** Absolute by the time this leaves here. */
  url: string;
  size: number;
  sha256: string;
  released: string;
}

const origin = apiBase.replace(/\/api\/v1$/, '');

const DISMISSED_KEY = 'storageapp.updateDismissed';

/** The newer release, or nothing at all — including when the check itself
 *  failed. A phone out of signal is not a thing to report. */
export async function newerRelease(): Promise<Release | null> {
  if (!isNative) return null;

  try {
    // Never from the cache: the whole question is what is on the server now.
    const response = await fetch(`${origin}/app/latest.json`, { cache: 'no-store' });
    if (!response.ok) return null;

    const release = (await response.json()) as Release;
    if (!release?.version) return null;
    if (versionCode(release.version) <= versionCode(APP_VERSION)) return null;

    return {
      ...release,
      url: /^https?:/.test(release.url) ? release.url : origin + release.url,
    };
  } catch {
    return null;
  }
}

/** Told once. Somebody who said "not now" meant this version, not all of them —
 *  the next release asks again. */
export function isDismissed(version: string): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === version;
  } catch {
    return false;
  }
}

export function dismiss(version: string): void {
  try {
    localStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // Then it asks again next time, which is the harmless direction to fail in.
  }
}
